import dayjs from 'dayjs';
import 'dayjs/locale/de';
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Language } from 'shared';
import LogoImage from '../../assets/SchambeckLogo.png';
import ArrowRight from '../../assets/arrow-right.svg?react';
import ArrowDown from '../../assets/chevron-down.svg?react';
import CloseIcon from '../../assets/close.svg?react';
import CzechFlag from '../../assets/cz.svg?react';
import GermanFlag from '../../assets/de.svg?react';
import LogoutIcon from '../../assets/logout.svg?react';
import MenuIcon from '../../assets/menu.svg?react';
import NavigationArrow from '../../assets/navigation-arrow.svg?react';
import UpArrow from '../../assets/up_arrow.svg?react';
import { Dropdown } from '../Dropdown/Dropdown';
import { EntryGroup, LayoutProps } from './Layout.props';
import './Layout.scss';
import { sortEntryGroups } from './Layout.utils';
export const Layout: React.FC<LayoutProps> = ({
  children,
  entryGroups,
  activeMenuId,
  isMenuOpen,
  toggleMenuOpen,
  logout,
  version,
  navigateHome,
  backendVersion,
  className,
  loggedInUser,
  allAvailableOffices,
  selectedOffices,
  onOfficeSelect,
  footNote,
  updateLanguage,
}) => {
  dayjs.locale('de');
  //holds the sorted entry group
  const sortedMenuGroups: EntryGroup[] = useMemo(() => {
    return sortEntryGroups(entryGroups);
  }, [entryGroups]);

  const [activeMenuGroups, setActiveMenuGroups] = useState<Set<string>>(new Set());

  /**
   * decides if the page is on production or development
   * used to set different backgroundColor for menu in development
   */
  const isDev: boolean =
    window.location.hostname.includes('dev') || window.location.hostname.includes('localhost');

  //Helper to open/close groups onClick
  const addOrRemoveMenuGroups = (groupId: string): void => {
    const groupsToEdit = new Set(activeMenuGroups);
    if (groupsToEdit.has(groupId)) groupsToEdit.delete(groupId);
    else groupsToEdit.add(groupId);
    setActiveMenuGroups(groupsToEdit);
  };

  /**
   * Helper to check if a group contains the activeMenuId
   * @param group to check the entries for
   * @returns boolean if the group is active or not
   */
  const groupContainsActiveMenuId = (group: EntryGroup): boolean => {
    return group.menuEntries.some((entry) => entry.id === activeMenuId);
  };

  //useEffect to scroll back to top on page change
  useEffect(() => {
    const content = document.getElementById('layout-component-content-wrapper');
    if (content && content.scrollTop != 0) content.scrollTop = 0;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [window.location.pathname]);

  return (
    <div className={['layout-component__wrapper', className].join(' ')}>
      <div
        style={{ backgroundColor: isDev ? 'lightblue' : '' }}
        className={['layout-component__menu__wrapper', isMenuOpen ? 'open' : 'closed'].join(' ')}
      >
        <div className="layout-component__menu__wrapper__head">
          <div
            className={[
              'layout-component__menu__wrapper__head__logo',
              isMenuOpen ? 'hide-on-small' : '',
            ].join(' ')}
          >
            <img
              className="home-logo"
              src={LogoImage}
              height={40}
              width={40}
              alt="Logo-Schambeck"
              onClick={navigateHome}
            />

            <p
              className={[
                'layout-component__menu__wrapper__head__title',
                isMenuOpen ? '' : 'hide-on-big',
              ].join(' ')}
            >
              SAMI
            </p>
          </div>
          {isMenuOpen ? (
            <CloseIcon
              width={20}
              height={20}
              className="layout-component__menu__wrapper__head__close hide-on-big"
              onClick={() => toggleMenuOpen(!isMenuOpen)}
            />
          ) : (
            <MenuIcon
              className="hide-on-big"
              width={20}
              height={40}
              onClick={() => toggleMenuOpen(!isMenuOpen)}
            />
          )}
        </div>
        {isMenuOpen && (
          <Dropdown
            options={allAvailableOffices}
            selectedMultiOptions={selectedOffices}
            multi
            smaller
            onChangeMultiple={onOfficeSelect}
          />
        )}
        <div className="layout-component__menu__entries" data-testid="layout-menu-entries">
          {sortedMenuGroups.map((entryGroup) =>
            entryGroup.menuEntries.length === 1 ? (
              <Link
                key={`layout-component-group-link-${entryGroup.id}`}
                to={entryGroup.menuEntries[0].path}
                onClick={entryGroup.menuEntries[0].onClick}
              >
                <div
                  key={`layout-component-group-${entryGroup.id}`}
                  className="layout-component__menu__menu-group__header"
                >
                  <div
                    style={{
                      color: groupContainsActiveMenuId(entryGroup) ? '#097be4' : 'black',
                    }}
                  >
                    {entryGroup.icon}
                  </div>
                  <p
                    className={[
                      'layout-component__menu__menu-group__header__title',
                      'hide-on-small',
                      groupContainsActiveMenuId(entryGroup) ? 'active' : '',
                    ].join(' ')}
                  >
                    {isMenuOpen ? entryGroup.groupName : ''}
                  </p>
                </div>
              </Link>
            ) : (
              <div
                data-testid="layout-menu-entry"
                className={['hide-on-small', 'layout-component__menu__entry__wrapper'].join(' ')}
                key={`menu_entry_desktop_${entryGroup.id}`}
              >
                <div
                  className="layout-component__menu__menu-group__header"
                  onClick={() => {
                    addOrRemoveMenuGroups(entryGroup.id);
                    if (!isMenuOpen) toggleMenuOpen(true);
                  }}
                >
                  <div
                    style={{
                      color: groupContainsActiveMenuId(entryGroup) ? '#097be4' : 'black',
                    }}
                  >
                    {entryGroup.icon}
                  </div>
                  <p
                    className={[
                      'layout-component__menu__menu-group__header__title',
                      groupContainsActiveMenuId(entryGroup) ? 'active' : '',
                    ].join(' ')}
                  >
                    {isMenuOpen ? entryGroup.groupName : ''}
                  </p>
                  {isMenuOpen ? (
                    groupContainsActiveMenuId(entryGroup) ? (
                      <UpArrow width={20} />
                    ) : (
                      <ArrowDown width={15} />
                    )
                  ) : (
                    ''
                  )}
                </div>
                <div
                  className={[
                    'layout-component__menu__menu-group__content',
                    activeMenuGroups.has(entryGroup.id) ? '' : 'hide-on-big',
                  ].join(' ')}
                >
                  <div className="layout-component__menu__menu-group__entries">
                    {entryGroup.menuEntries.map((entry, index) => {
                      return (
                        <Link
                          to={entry.path}
                          onClick={entry.onClick}
                          key={`entry-group-${entryGroup.id}-entry-${entry.id}`}
                        >
                          <div
                            className="layout-component__menu__menu-group__entries__entry"
                            style={{
                              display: isMenuOpen ? '' : 'none',
                            }}
                          >
                            <div
                              className={[
                                'layout-component__menu__menu-group__entry-title',
                                activeMenuId === entry.id ? 'active' : '',
                              ].join(' ')}
                            >
                              <p
                                className={
                                  activeMenuId === entry.id
                                    ? 'layout-component__menu__menu-group__entry-active'
                                    : 'layout-component__menu__menu-group__entry-not-active'
                                }
                              >
                                {entry.title}
                              </p>
                              <div className={index === 0 ? 'line' : 'line long'}></div>
                            </div>
                          </div>
                        </Link>
                      );
                    })}
                  </div>
                </div>
              </div>
            ),
          )}
        </div>
        <div className="layout-component__menu__footer-buttons">
          {isMenuOpen && (
            <div
              onClick={logout}
              className="layout-component__menu__footer-buttons__logout__wrapper"
            >
              <LogoutIcon className="layout-component__menu__footer-buttons__logout__icon" />
              <p className="layout-component__menu__footer-buttons__logout__title">Logout</p>
            </div>
          )}
          <div className="layout-component__menu__footer-buttons__content-wrapper">
            <ArrowRight
              width={20}
              onClick={() => toggleMenuOpen(!isMenuOpen)}
              className={['hide-on-small', isMenuOpen ? 'open' : 'closed'].join(' ')}
            />
            {isMenuOpen && (
              <div className="layout-component__menu__footer-buttons__version-wrapper">
                <p className="layout-component__menu__version">{loggedInUser}</p>
                <p className="layout-component__menu__version">{version}</p>
                <p className="layout-component__menu__version">{backendVersion}</p>
                <div className="layout-component__menu__footer-buttons__version-wrapper__flag-wrapper">
                  <GermanFlag
                    className="layout-component__menu__footer-buttons__version-wrapper__flag-wrapper__flag"
                    onClick={() => updateLanguage(Language.DE)}
                  />
                  <CzechFlag
                    className="layout-component__menu__footer-buttons__version-wrapper__flag-wrapper__flag"
                    onClick={() => updateLanguage(Language.CZ)}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <div
        className={['layout-component__content__wrapper', isMenuOpen ? '' : 'menu-closed'].join(
          ' ',
        )}
        id="layout-component-content-wrapper"
      >
        {children}
        <p className="footnote">{footNote}</p>
        <NavigationArrow
          className="navigation-arrow"
          onClick={() => {
            const wrapper: HTMLElement | null = document.getElementById(
              'layout-component-content-wrapper',
            );
            wrapper?.scroll({ top: wrapper.scrollHeight });
          }}
        />
      </div>
    </div>
  );
};
